/**
 * The feedback form — category, message, the current story, and P5's `request_id`.
 *
 * Mounted once by `App`, outside the error boundary, so it survives the crash it is most
 * likely to be reporting.
 *
 * Two things about the shape:
 *
 * - **The request id is shown, not hidden.** A player who is told "this report carries the
 * error's trace id" understands why the report is worth sending, and one who copies it into
 * a Discord message has done us a favour. It is not editable and it is not asked for.
 * - **It closes on success and says so with a toast**, rather than turning into a "thank you"
 * screen. The player was in the middle of something; feedback is an interruption they
 * chose, and the shortest one is the best one. `POST /feedback` is a 204 — there is nothing
 * to show them.
 *
 * SES is in the sandbox (200/day, verified recipients only), so a send that succeeds here may
 * still not reach a mailbox. That is an infrastructure fact recorded in
 * `infrastructure/quest-v2-api/README.md`, and deliberately not something this dialog hedges
 * about: from the player's side the report was accepted, because it was.
 */
import { useState } from 'react';
import { describeApiError } from '@/api/errorMessages';
import { useSubmitFeedback } from '@/api/queries';
import type { FeedbackCategory } from '@/api/generated';
import { useFeedbackStore, type FeedbackContext } from '@/features/feedback/store';
import { useT } from '@/i18n/context';
import type { UiKey } from '@/i18n/en.generated';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { toastOk } from '@/ui/toast';

/** `FeedbackRequest.message` — `minLength: 1`, `maxLength: 5000`. */
export const MESSAGE_MAX = 5_000;

const CATEGORIES: { value: FeedbackCategory; label: UiKey }[] = [
  { value: 'bug', label: 'ui.feedback.category.bug' },
  { value: 'feedback', label: 'ui.feedback.category.feedback' },
  { value: 'suggestion', label: 'ui.feedback.category.suggestion' },
];

export function FeedbackDialog() {
  const context = useFeedbackStore((state) => state.context);

  // Mounted only while open, so the form's state is seeded from the context that opened it at
  // mount and thrown away on close. An error path arrives as a bug with the error's own words
  // already in the box; the navigation link arrives blank; and neither can inherit the other's
  // half-typed message, which is what a long-lived form synced by an effect would do.
  return context ? <FeedbackForm context={context} /> : null;
}

function FeedbackForm({ context }: { context: FeedbackContext }) {
  const t = useT();
  const send = useSubmitFeedback();

  const [category, setCategory] = useState<FeedbackCategory>(context.category);
  const [message, setMessage] = useState(context.prefill);
  const [failure, setFailure] = useState<string | null>(null);

  const close = (): void => {
    useFeedbackStore.getState().close();
  };

  const submit = (): void => {
    if (message.trim().length === 0) return;
    send.mutate(
      {
        category,
        message: message.trim().slice(0, MESSAGE_MAX),
        story_id: context.storyId,
        request_id: context.requestId,
        path: context.path,
      },
      {
        onSuccess: () => {
          toastOk(t('ui.feedback.sent'));
          close();
        },
        onError: (error: unknown) => {
          setFailure(describeApiError(t, error));
        },
      },
    );
  };

  return (
    <Dialog
      open
      onClose={close}
      title={t('ui.feedback.title')}
      footer={
        <>
          <Button variant="bare" size="action" onClick={close}>
            {t('ui.common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="action"
            disabled={send.isPending || message.trim().length === 0}
            onClick={submit}
          >
            {send.isPending ? t('ui.feedback.sending') : t('ui.feedback.send')}
          </Button>
        </>
      }
    >
      <fieldset className="mb-4 flex flex-wrap gap-2">
        <legend className="text-fg-muted mb-2 font-mono" style={{ fontSize: 'var(--text-meta)' }}>
          {t('ui.feedback.legend')}
        </legend>
        {CATEGORIES.map((option) => (
          <label
            key={option.value}
            className={`cursor-pointer border px-3 py-1.5 font-mono transition-colors ${
              category === option.value
                ? 'border-accent text-accent'
                : 'border-border text-fg-muted hover:border-fg-faint'
            }`}
            style={{ fontSize: 'var(--text-meta)' }}
          >
            <input
              type="radio"
              name="feedback_category"
              value={option.value}
              checked={category === option.value}
              onChange={() => {
                setCategory(option.value);
              }}
              className="sr-only"
            />
            {t(option.label)}
          </label>
        ))}
      </fieldset>

      <label
        htmlFor="feedback-message"
        className="text-fg-muted mb-2 block font-mono"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {t('ui.feedback.message')}
      </label>
      <textarea
        id="feedback-message"
        value={message}
        rows={6}
        maxLength={MESSAGE_MAX}
        onChange={(event) => {
          setMessage(event.target.value);
        }}
        className="border-border focus:border-accent text-fg placeholder:text-fg-faint w-full border bg-transparent px-3 py-2 transition-colors outline-none"
        style={{ fontSize: 'var(--text-action)', lineHeight: 1.5 }}
      />

      {context.requestId ? (
        <p className="text-fg-faint mt-3 font-mono" style={{ fontSize: 'var(--text-meta)' }}>
          {t('ui.feedback.trace', { request_id: context.requestId })}
        </p>
      ) : null}

      {failure ? (
        <p
          role="alert"
          className="mt-3 text-[color:var(--color-ramp-danger)]"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          {failure}
        </p>
      ) : null}
    </Dialog>
  );
}
